import { FC, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Source, Layer } from 'react-map-gl';
import bbox from '@turf/bbox';

import { Map as MapComponent } from 'components/map';
import { useDataTableContext } from 'components/data-table';
import { WellMapLayer } from 'components/well-map-layer';
import { mapColors } from 'components/map/map-colors';

import { afeCatalogMapConfigAtom, afeCatalogMapRefAtom } from './map-atoms';
import { useAfeWellGeometry } from './hooks';
import type { FlatAfeRow } from './types';
import styles from './afe-catalog-map.module.scss';

const US_CENTER_LONGITUDE = -98.5795;
const US_CENTER_LATITUDE = 39.8283;
const US_ZOOM = 4;

interface AfeCatalogMapProps {
  uwi: string | null;
}

export const AfeCatalogMap: FC<AfeCatalogMapProps> = ({ uwi }) => {
  const { isFilterPanelOpen } = useDataTableContext<FlatAfeRow>();
  const mapRef = useAtomValue(afeCatalogMapRefAtom);

  const { wellbores, intersectingUnits, isLoading } = useAfeWellGeometry(uwi);

  const showWellData = !!uwi;

  const producingWellbores = useMemo(
    () => wellbores.filter((wb) => wb.type === 'producing'),
    [wellbores]
  );
  const sidetrackWellbores = useMemo(
    () => wellbores.filter((wb) => wb.type === 'sidetrack'),
    [wellbores]
  );

  const selectedBounds = useMemo(() => {
    if (!showWellData) return null;
    const features: GeoJSON.Feature[] = [];
    intersectingUnits.forEach((unit) => {
      const geom = (unit.geojson as { geometry?: GeoJSON.Geometry } | null)
        ?.geometry;
      if (geom) {
        features.push({ type: 'Feature', properties: {}, geometry: geom });
      }
    });
    wellbores.forEach((wb) => {
      if (wb.survey?.geometry) features.push(wb.survey);
      if (wb.surfaceHole?.coordinates) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: wb.surfaceHole,
        });
      }
    });
    if (features.length === 0) return null;
    let computed: ReturnType<typeof bbox>;
    try {
      computed = bbox({ type: 'FeatureCollection', features });
    } catch {
      return null;
    }
    const [minLng, minLat, maxLng, maxLat] = computed;
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]];
  }, [showWellData, intersectingUnits, wellbores]);

  useEffect(() => {
    if (!mapRef) return;
    if (selectedBounds) {
      mapRef.getMap()?.fitBounds(selectedBounds, { padding: 60, maxZoom: 14 });
    } else if (!uwi) {
      mapRef.getMap()?.easeTo({
        center: [US_CENTER_LONGITUDE, US_CENTER_LATITUDE],
        zoom: US_ZOOM,
      });
    }
  }, [mapRef, selectedBounds, uwi]);

  useEffect(() => {
    if (!mapRef) return;
    mapRef.getMap()?.resize();
  }, [mapRef, isFilterPanelOpen, isLoading]);

  return (
    <div className={styles.mapContainer}>
      <MapComponent
        latitude={US_CENTER_LATITUDE}
        longitude={US_CENTER_LONGITUDE}
        zoom={US_ZOOM}
        showControls
        showMapFilters
        blockPopups
        disableWellsLayer
        mapConfigAtom={afeCatalogMapConfigAtom}
        refAtom={afeCatalogMapRefAtom}
        className={styles.mapFill}
      >
        {intersectingUnits.map((unit) => {
          const geom = (unit.geojson as { geometry?: GeoJSON.Geometry } | null)
            ?.geometry;
          if (!geom) return null;
          const unitFeature: GeoJSON.Feature = {
            type: 'Feature',
            properties: {},
            geometry: geom,
          };
          return (
            <Source
              key={`afe-cat-unit-${unit.id}`}
              type="geojson"
              data={unitFeature}
            >
              <Layer
                id={`afe-cat-unit-fill-${unit.id}`}
                type="fill"
                paint={{
                  'fill-color': unit.assignedToUnit
                    ? mapColors.assignedUnitFill
                    : mapColors.nonAssignedUnitFill,
                  'fill-opacity': unit.assignedToUnit ? 0.6 : 0.4,
                }}
              />
              <Layer
                id={`afe-cat-unit-outline-${unit.id}`}
                type="line"
                paint={{
                  'line-color': unit.assignedToUnit
                    ? mapColors.assignedUnitBorder
                    : mapColors.nonAssignedUnitBorder,
                  'line-width': 2,
                  'line-opacity': 1,
                }}
              />
            </Source>
          );
        })}

        {producingWellbores.map((wb, idx) => {
          const wbId = wb.uwi14 ?? `idx-${idx}`;
          return (
            <WellMapLayer
              key={`afe-cat-producing-${wbId}`}
              id={`afe-cat-producing-${wbId}`}
              wellbore={wb}
              mapConfigAtom={afeCatalogMapConfigAtom}
            />
          );
        })}

        {sidetrackWellbores.map((wb, idx) => {
          const wbId = wb.uwi14 ?? `idx-${idx}`;
          return (
            <WellMapLayer
              key={`afe-cat-sidetrack-${wbId}`}
              id={`afe-cat-sidetrack-${wbId}`}
              wellbore={wb}
              mapConfigAtom={afeCatalogMapConfigAtom}
            />
          );
        })}
      </MapComponent>
    </div>
  );
};
